import { useEffect, useState } from 'react';
import { Button, Dropdown, SidePanel, useToast } from '../../../ui';
import { supabase } from '../../../../lib/supabase';
import type { Json } from '../../../../lib/database.types';
import { faint, fmtCompact, fmtDate, linkStyle, STATUS, textareaStyle, TYPE_LABELS } from './format';
import { DRBadge, Field, MetricRow, PanelSection, ReasonField, RelevanceMeter, StatusBadge } from './shared';

type Opportunity = {
  id: string;
  opportunity_type: string;
  target_url: string;
  target_domain: string;
  anchor_suggestion: string | null;
  target_page_url: string | null;
  target_dr: number | null;
  target_traffic: number | null;
  relevance_score: number | null;
  source: string;
  discovered_via: Json | null;
  brief: Json | null;
  status: string;
  status_reason: string | null;
  outreach_date: string | null;
  response_date: string | null;
  acquired_link_id: string | null;
  notes: string | null;
  created_at: string;
};

interface OpportunityDetailPanelProps {
  opportunityId: string;
  onClose: () => void;
  onUpdate: () => void;
}

const STATUS_OPTIONS = Object.entries(STATUS).map(([value, s]) => ({
  value,
  label: value === 'outreach_drafted' ? 'Outreach drafted' : s.label,
}));

export default function OpportunityDetailPanel({ opportunityId, onClose, onUpdate }: OpportunityDetailPanelProps) {
  const toast = useToast();
  const [opp, setOpp] = useState<Opportunity | null>(null);
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('');
  const [statusReason, setStatusReason] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [ignoring, setIgnoring] = useState(false);
  const [showSource, setShowSource] = useState(false);

  useEffect(() => {
    const load = async () => {
      const { data, error } = await supabase.from('backlink_opportunities').select('*').eq('id', opportunityId).single();
      if (error) {
        console.error('Error loading opportunity:', error);
        toast.error('Failed to load opportunity');
        return;
      }
      setOpp(data);
      setNotes(data.notes || '');
      setStatus(data.status);
      setStatusReason(data.status_reason);
    };
    load();
  }, [opportunityId, toast]);

  const save = async (patch: { status: string; notes: string; status_reason: string | null }) => {
    if (!opp) return;
    setSaving(true);
    try {
      const { error } = await supabase.from('backlink_opportunities').update(patch).eq('id', opp.id);
      if (error) throw error;
      toast.success('Opportunity updated');
      setOpp({ ...opp, ...patch });
      onUpdate();
    } catch (err) {
      console.error('Error updating opportunity:', err);
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const dirty = !!opp && (notes !== (opp.notes || '') || status !== opp.status);
  const pitch = opp ? briefPitch(opp.brief) : null;

  return (
    <SidePanel
      open
      onClose={onClose}
      width={560}
      eyebrow={opp ? `Opportunity · ${TYPE_LABELS[opp.opportunity_type] || opp.opportunity_type}` : 'Opportunity'}
      title={opp?.target_domain ?? 'Loading…'}
      footer={
        opp ? (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {ignoring ? (
              <ReasonField
                placeholder="Why ignore it? (feeds the deny rules)"
                confirmLabel="Ignore"
                onConfirm={(reason) => {
                  setIgnoring(false);
                  setStatusReason(reason);
                  save({ status: 'ignored', notes, status_reason: reason }).then(() => setStatus('ignored'));
                }}
                onCancel={() => setIgnoring(false)}
              />
            ) : opp.status === 'new' && !dirty ? (
              <>
                <Button variant="ghost" icon="x" onClick={() => setIgnoring(true)} disabled={saving}>
                  Ignore
                </Button>
                <span style={{ flex: 1 }} />
                <Button
                  variant="primary"
                  icon="check"
                  onClick={() => save({ status: 'approved', notes, status_reason: statusReason }).then(() => setStatus('approved'))}
                  disabled={saving}
                >
                  Approve
                </Button>
              </>
            ) : (
              <>
                <span style={{ flex: 1 }} />
                <Button
                  variant="ghost"
                  onClick={() => {
                    setNotes(opp.notes || '');
                    setStatus(opp.status);
                  }}
                  disabled={saving || !dirty}
                >
                  Discard
                </Button>
                <Button variant="primary" onClick={() => save({ status, notes, status_reason: statusReason })} disabled={saving || !dirty}>
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </>
            )}
          </div>
        ) : null
      }
    >
      {opp && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-5)' }}>
            <StatusBadge status={opp.status} />
            <span style={{ ...faint, fontSize: 'var(--text-sm)' }}>Found {fmtDate(opp.created_at)}</span>
          </div>

          <MetricRow
            items={[
              { label: 'DR', value: <DRBadge dr={opp.target_dr} /> },
              { label: 'Traffic', value: fmtCompact(opp.target_traffic) },
              { label: 'Relevance', value: <RelevanceMeter score={opp.relevance_score} /> },
            ]}
          />

          {pitch && (
            <PanelSection title="Why this link">
              <p style={{ margin: 0, fontSize: 'var(--text-sm)', lineHeight: 1.6, color: 'var(--text-body)' }}>{pitch}</p>
            </PanelSection>
          )}

          <PanelSection title="The ask">
            <Field label="Their page">
              <a href={opp.target_url} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                {opp.target_url}
              </a>
            </Field>
            <Field label="Link to">
              {opp.target_page_url ? (
                <a href={opp.target_page_url} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                  {opp.target_page_url}
                </a>
              ) : (
                <span style={faint}>Not chosen yet</span>
              )}
            </Field>
            <Field label="Anchor">{opp.anchor_suggestion || <span style={faint}>No suggestion</span>}</Field>
          </PanelSection>

          <PanelSection title="Workflow">
            <Field label="Status">
              <Dropdown size="sm" options={STATUS_OPTIONS} value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: '200px' }} />
            </Field>
            {opp.outreach_date && <Field label="Pitched">{fmtDate(opp.outreach_date)}</Field>}
            {opp.response_date && <Field label="Replied">{fmtDate(opp.response_date)}</Field>}
            {statusReason && <Field label="Reason">{statusReason}</Field>}
          </PanelSection>

          <PanelSection title="Notes">
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Contact, angle, follow-up dates…" style={textareaStyle} />
          </PanelSection>

          {opp.discovered_via && (
            <div>
              <Button size="sm" variant="ghost" icon={showSource ? 'chevron-down' : 'chevron-right'} onClick={() => setShowSource((s) => !s)}>
                How we found it
              </Button>
              {showSource && (
                <pre
                  style={{
                    marginTop: 'var(--space-2)',
                    fontSize: 'var(--text-xs)',
                    fontFamily: 'var(--font-mono)',
                    background: 'var(--surface-raised)',
                    padding: 'var(--space-3)',
                    borderRadius: 'var(--radius-md)',
                    overflow: 'auto',
                    color: 'var(--text-muted)',
                  }}
                >
                  {JSON.stringify(opp.discovered_via, null, 2)}
                </pre>
              )}
            </div>
          )}
        </>
      )}
    </SidePanel>
  );
}

function briefPitch(brief: Json | null): string | null {
  if (!brief || typeof brief !== 'object' || Array.isArray(brief)) return null;
  return typeof brief.pitch === 'string' ? brief.pitch : null;
}
